import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { connectSocket } from '../socket';

const CELL = 30;
const SIZE = 15 * CELL;
const COLOR = { red: '#e24b4a', green: '#1d9e75', yellow: '#e0a012', blue: '#2563b8' };
const COLOR_LABEL = { red: 'Đỏ', green: 'Lục', yellow: 'Vàng', blue: 'Lam' };

// Vòng ngoài 52 ô (col,row) theo bàn ludo 15x15, theo chiều kim đồng hồ.
const PATH = [
  [1, 6], [2, 6], [3, 6], [4, 6], [5, 6], [6, 5], [6, 4], [6, 3], [6, 2], [6, 1], [6, 0], [7, 0],
  [8, 0], [8, 1], [8, 2], [8, 3], [8, 4], [8, 5], [9, 6], [10, 6], [11, 6], [12, 6], [13, 6], [14, 6], [14, 7],
  [14, 8], [13, 8], [12, 8], [11, 8], [10, 8], [9, 8], [8, 9], [8, 10], [8, 11], [8, 12], [8, 13], [8, 14], [7, 14],
  [6, 14], [6, 13], [6, 12], [6, 11], [6, 10], [6, 9], [5, 8], [4, 8], [3, 8], [2, 8], [1, 8], [0, 8], [0, 7], [0, 6],
];
const START_INDEX = { red: 0, green: 13, yellow: 26, blue: 39 };
const HOME = {
  red: [[1, 7], [2, 7], [3, 7], [4, 7], [5, 7], [6, 7]],
  green: [[7, 1], [7, 2], [7, 3], [7, 4], [7, 5], [7, 6]],
  yellow: [[13, 7], [12, 7], [11, 7], [10, 7], [9, 7], [8, 7]],
  blue: [[7, 13], [7, 12], [7, 11], [7, 10], [7, 9], [7, 8]],
};
const YARD = {
  red: [[1, 1], [3, 1], [1, 3], [3, 3]],
  green: [[10, 1], [12, 1], [10, 3], [12, 3]],
  yellow: [[10, 10], [12, 10], [10, 12], [12, 12]],
  blue: [[1, 10], [3, 10], [1, 12], [3, 12]],
};
const YARD_BOX = { red: [0, 0], green: [9, 0], yellow: [9, 9], blue: [0, 9] };

const center = ([c, r]) => ({ x: (c + 0.5) * CELL, y: (r + 0.5) * CELL });
function pieceCenter(color, pos, idx) {
  if (pos === -1) return center(YARD[color][idx]);
  if (pos <= 50) return center(PATH[(START_INDEX[color] + pos) % 52]);
  return center(HOME[color][pos - 51]);
}

export default function CaNguaPage() {
  const { me } = useAuth();
  const myId = String(me.id || me._id);
  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState(null);
  const [joinId, setJoinId] = useState('');
  const [err, setErr] = useState('');
  const sockRef = useRef(null);

  useEffect(() => {
    const socket = connectSocket();
    sockRef.current = socket;
    if (!socket) return;
    const onState = (r) => setRoom(r);
    const onRooms = (list) => setRooms(list || []);
    socket.on('cangua:state', onState);
    socket.on('cangua:rooms', onRooms);
    socket.emit('cangua:rooms', (list) => setRooms(list || []));
    return () => {
      socket.off('cangua:state', onState);
      socket.off('cangua:rooms', onRooms);
    };
  }, []);

  // Truyền đúng số tham số (không kèm undefined ở cuối) để socket.io nhận ack.
  const send = (...args) => sockRef.current?.emit(...args);

  function create() {
    send('cangua:create', (res) => res?.ok && setRoom(res.room));
  }
  function join(id) {
    setErr('');
    send('cangua:join', { roomId: id }, (res) => {
      if (res?.ok) setRoom(res.room);
      else setErr(res?.error || 'Không vào được phòng');
    });
  }
  function leave() {
    if (room) send('cangua:leave', { roomId: room.id });
    setRoom(null);
  }
  const start = () => send('cangua:start', { roomId: room.id });
  const roll = () => send('cangua:roll', { roomId: room.id });
  const move = (piece) => send('cangua:move', { roomId: room.id, piece });

  // ===== Sảnh =====
  if (!room) {
    return (
      <>
        <div className="page-head">
          <div>
            <h2>🎲 Cá Ngựa online</h2>
            <div className="sub">Tạo phòng rồi rủ đồng nghiệp cùng chơi</div>
          </div>
        </div>
        <div className="card">
          <div className="row" style={{ gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <button className="btn" onClick={create}>+ Tạo phòng mới</button>
            <div className="row" style={{ gap: 6 }}>
              <input
                style={{ width: 140 }}
                placeholder="Mã phòng…"
                value={joinId}
                onChange={(e) => setJoinId(e.target.value.toUpperCase())}
              />
              <button className="btn ghost" disabled={!joinId.trim()} onClick={() => join(joinId.trim())}>
                Vào phòng
              </button>
            </div>
          </div>
          {err && <div className="small" style={{ color: 'var(--danger)', marginBottom: 10 }}>{err}</div>}
          <h3 style={{ fontSize: 14, marginBottom: 8 }}>Phòng đang mở ({rooms.length})</h3>
          {!rooms.length ? (
            <div className="empty">Chưa có phòng nào. Tạo phòng mới để bắt đầu!</div>
          ) : (
            <table>
              <thead>
                <tr><th>Mã phòng</th><th>Chủ phòng</th><th className="c">Người chơi</th><th></th></tr>
              </thead>
              <tbody>
                {rooms.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{r.id}</td>
                    <td>{r.hostName}</td>
                    <td className="c">{r.count}/4</td>
                    <td className="r">
                      <button className="btn sm" disabled={r.count >= 4} onClick={() => join(r.id)}>Vào</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </>
    );
  }

  // ===== Trong phòng =====
  const meIdx = room.players.findIndex((p) => p.userId === myId);
  const isHost = room.hostId === myId;
  const curPlayer = room.started ? room.players[room.turnIndex] : null;
  const myTurn = curPlayer && curPlayer.userId === myId && !room.winner;
  const canRoll = myTurn && !room.awaitingMove && room.dice == null;
  const winner = room.winner ? room.players.find((p) => p.userId === room.winner) : null;

  return (
    <>
      <div className="page-head">
        <div>
          <h2>🎲 Phòng <span style={{ fontFamily: 'var(--font-mono)' }}>{room.id}</span></h2>
          <div className="sub">{room.started ? 'Đang chơi' : 'Đang chờ người chơi'}</div>
        </div>
        <button className="btn ghost sm" onClick={leave}>← Rời phòng</button>
      </div>

      <div className="cangua-wrap">
        <div className="card cangua-board-card">
          <Board room={room} myId={myId} myTurn={myTurn} onMove={move} />
        </div>

        <div className="card cangua-side">
          {winner ? (
            <div className="cangua-winner">🏆 <b>{winner.name}</b> ({COLOR_LABEL[winner.color]}) thắng!</div>
          ) : room.started ? (
            <div className="cangua-status">
              {myTurn ? (
                room.awaitingMove ? (
                  <div>Tới lượt bạn — <b>chọn quân sáng</b> để đi</div>
                ) : (
                  <div>Tới lượt bạn — hãy lắc xúc xắc</div>
                )
              ) : (
                <div>Đến lượt <b style={{ color: COLOR[curPlayer?.color] }}>{curPlayer?.name}</b>…</div>
              )}
            </div>
          ) : (
            <div className="cangua-status">Chờ chủ phòng bắt đầu (cần ≥ 2 người)</div>
          )}

          <div className="cangua-dice-row">
            <Dice value={room.dice} />
            {room.started && !room.winner && (
              <button className="btn" disabled={!canRoll} onClick={roll}>
                {room.awaitingMove && myTurn ? 'Chọn quân…' : 'Lắc xúc xắc'}
              </button>
            )}
          </div>

          <h3 style={{ fontSize: 13, margin: '14px 0 6px' }}>Người chơi ({room.players.length}/4)</h3>
          <div className="cangua-players">
            {room.players.map((p, i) => (
              <div key={p.userId} className={'cangua-player' + (room.started && i === room.turnIndex ? ' turn' : '')}>
                <span className="cangua-dot" style={{ background: COLOR[p.color] }} />
                <span className="cangua-pname">{p.name}{p.userId === myId ? ' (bạn)' : ''}</span>
                <span className="small muted">{p.pieces.filter((x) => x === 56).length}/4 về đích</span>
                {!p.connected && <span className="small" style={{ color: 'var(--danger)' }}>mất KN</span>}
              </div>
            ))}
          </div>

          {!room.started && isHost && (
            <button className="btn full" style={{ marginTop: 14 }} disabled={room.players.length < 2} onClick={start}>
              ▶ Bắt đầu ván
            </button>
          )}
          {!room.started && (
            <div className="small muted" style={{ marginTop: 10 }}>
              Chia sẻ mã phòng <b style={{ fontFamily: 'var(--font-mono)' }}>{room.id}</b> cho đồng nghiệp để cùng vào.
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Xúc xắc hiển thị bằng số chấm.
function Dice({ value }) {
  const pips = {
    1: [4], 2: [0, 8], 3: [0, 4, 8], 4: [0, 2, 6, 8], 5: [0, 2, 4, 6, 8], 6: [0, 2, 3, 5, 6, 8],
  };
  return (
    <div className="cangua-dice">
      {value ? (
        Array.from({ length: 9 }).map((_, i) => (
          <span key={i} className="cangua-pip" style={{ visibility: (pips[value] || []).includes(i) ? 'visible' : 'hidden' }} />
        ))
      ) : (
        <span className="cangua-dice-empty">🎲</span>
      )}
    </div>
  );
}

function Board({ room, myId, myTurn, onMove }) {
  const legal = myTurn && room.awaitingMove ? room.legalMoves : [];
  const meColor = room.players.find((p) => p.userId === myId)?.color;

  return (
    <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="cangua-svg" role="img" aria-label="Bàn cờ cá ngựa">
      {/* Nền */}
      <rect x="0" y="0" width={SIZE} height={SIZE} rx="10" fill="var(--panel2)" />

      {/* Chuồng 4 góc */}
      {Object.entries(YARD_BOX).map(([color, [c, r]]) => (
        <g key={color}>
          <rect x={c * CELL + 3} y={r * CELL + 3} width={6 * CELL - 6} height={6 * CELL - 6} rx="12" fill={COLOR[color]} opacity="0.16" stroke={COLOR[color]} strokeWidth="2" />
          {YARD[color].map((cell, i) => {
            const p = center(cell);
            return <circle key={i} cx={p.x} cy={p.y} r="9" fill="none" stroke={COLOR[color]} strokeWidth="2" opacity="0.5" />;
          })}
        </g>
      ))}

      {/* Ô vòng ngoài */}
      {PATH.map(([c, r], i) => {
        const startColor = Object.keys(START_INDEX).find((k) => START_INDEX[k] === i);
        return (
          <rect key={i} x={c * CELL + 1.5} y={r * CELL + 1.5} width={CELL - 3} height={CELL - 3} rx="5"
            fill={startColor ? COLOR[startColor] : 'var(--panel)'} opacity={startColor ? 0.85 : 1}
            stroke="var(--border)" strokeWidth="1" />
        );
      })}

      {/* Đường về đích (6 ô mỗi màu) */}
      {Object.entries(HOME).map(([color, cells]) =>
        cells.map(([c, r], i) => (
          <rect key={color + i} x={c * CELL + 1.5} y={r * CELL + 1.5} width={CELL - 3} height={CELL - 3} rx="5"
            fill={COLOR[color]} opacity={i === 5 ? 0.95 : 0.4} stroke="var(--border)" strokeWidth="1" />
        ))
      )}

      {/* Quân của tất cả người chơi */}
      {room.players.map((p) =>
        p.pieces.map((pos, idx) => {
          const { x, y } = pieceCenter(p.color, pos, idx);
          const isMine = p.userId === myId;
          const clickable = isMine && legal.includes(idx);
          // lệch nhẹ khi nhiều quân chồng ô
          const dx = pos >= 0 ? (idx % 2) * 5 - 2.5 : 0;
          const dy = pos >= 0 ? (idx > 1 ? 1 : -1) * 2 : 0;
          return (
            <g key={p.color + idx} style={{ cursor: clickable ? 'pointer' : 'default' }} onClick={clickable ? () => onMove(idx) : undefined}>
              {clickable && <circle cx={x + dx} cy={y + dy} r="13" fill="none" stroke={COLOR[p.color]} strokeWidth="2.5" className="cangua-legal" />}
              <circle cx={x + dx} cy={y + dy} r="9" fill={COLOR[p.color]} stroke="#fff" strokeWidth="2" />
            </g>
          );
        })
      )}
      {/* Khung tô đậm màu của mình */}
      {meColor && (
        <text x={SIZE - 6} y={SIZE - 8} textAnchor="end" fontSize="11" fill={COLOR[meColor]}>
          Bạn là {COLOR_LABEL[meColor]}
        </text>
      )}
    </svg>
  );
}
